import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "../firebase";
import { ref, onValue, set, get } from "firebase/database";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [greeting, setGreeting] = useState("");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBooks: 0,
    totalTeachers: 0,
    booksIssued: 0,
    pendingReturns: 0,
    totalFine: 0
  });
  const [topBooks, setTopBooks] = useState([]);
  const [adminName, setAdminName] = useState("Admin");
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [elibraryCount, setElibraryCount] = useState(0);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Set greeting based on time of day
  useEffect(() => {
    const hour = currentTime.getHours();
    if (hour < 12) setGreeting("Good Morning");
    else if (hour < 17) setGreeting("Good Afternoon");
    else setGreeting("Good Evening");
  }, [currentTime]);

  // Fetch admin data from Firebase
  useEffect(() => {
    const fetchAdminData = async () => {
      setLoading(true);
      
      // Get admin info from localStorage
      const storedName = localStorage.getItem("userName");
      if (storedName) {
        setAdminName(storedName);
      }

      // Fetch total books
      const booksRef = ref(db, 'books');
      onValue(booksRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const booksList = Object.values(data);
          const totalCopies = booksList.reduce((sum, book) => sum + (book.total || 0), 0);
          const issuedBooks = booksList.reduce((sum, book) => sum + ((book.total || 0) - (book.available || 0)), 0);
          
          setStats(prev => ({
            ...prev,
            totalBooks: totalCopies,
            booksIssued: issuedBooks
          }));

          // Calculate top borrowed books
          const topBorrowed = booksList
            .sort((a, b) => (b.issuedCount || 0) - (a.issuedCount || 0))
            .slice(0, 5)
            .map((book, index) => ({
              rank: index + 1,
              name: book.name || "Unknown Book",
              borrows: book.issuedCount || 0
            }));
          setTopBooks(topBorrowed.length > 0 ? topBorrowed : [
            { rank: 1, name: "No books borrowed yet", borrows: 0 }
          ]);
        }
      });

      // Fetch total teachers
      const usersRef = ref(db, 'users');
      onValue(usersRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const teachersList = Object.values(data).filter(user => user.role === "teacher");
          setStats(prev => ({
            ...prev,
            totalTeachers: teachersList.length
          }));
        }
      });

      // Fetch transactions for stats
      const transactionsRef = ref(db, 'transactions');
      onValue(transactionsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const transactions = Object.values(data);
          const pendingReturns = transactions.filter(t => t.status === "issued").length;
          const totalFine = transactions.reduce((sum, t) => sum + (t.fine || 0), 0);
          
          setStats(prev => ({
            ...prev,
            pendingReturns: pendingReturns,
            totalFine: totalFine
          }));
          setTotalTransactions(transactions.length);
        }
      });

      // Fetch E-Library count
      const elibraryRef = ref(db, 'elibrary');
      onValue(elibraryRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setElibraryCount(Object.keys(data).length);
        } else {
          setElibraryCount(0);
        }
      });
    };

    fetchAdminData();
    setTimeout(() => setLoading(false), 1000);
  }, []);

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.clear();
      navigate("/");
    }
  };

  // Bottom navigation items
  const bottomNavItems = [
    { name: "Dashboard", path: "/admin-dashboard", icon: "🏠", activeIcon: "🏠" },
    { name: "Books", path: "/books", icon: "📚", activeIcon: "📚" },
    { name: "Teachers", path: "/teachers", icon: "👩‍🏫", activeIcon: "👩‍🏫" },
    { name: "Issue/Return", path: "/manage-books", icon: "🔄", activeIcon: "🔄" },
    { name: "Reports", path: "/reports", icon: "📊", activeIcon: "📊" },
    { name: "Settings", path: "/settings", icon: "⚙️", activeIcon: "⚙️" }
  ];

  const quickActions = [
    { 
      name: "Manage Teachers", 
      path: "/teachers", 
      icon: "👩‍🏫", 
      color: "#667eea",
      description: "Add, edit or remove teachers"
    },
    { 
      name: "Issue/Return Book", 
      path: "/manage-books", 
      icon: "🔄", 
      color: "#48bb78",
      description: "Issue or return books"
    },
    { 
      name: "Manage Books", 
      path: "/books", 
      icon: "📚", 
      color: "#9f7aea",
      description: "Add, edit or remove books"
    },
    { 
      name: "E-Library", 
      path: "/admin-elibrary", 
      icon: "📖", 
      color: "#38b2ac",
      description: "Manage digital resources & PDFs"
    },
    { 
      name: "View Reports", 
      path: "/reports", 
      icon: "📊", 
      color: "#e53e3e",
      description: "View analytics & reports"
    },
    { 
      name: "Library Settings", 
      path: "/settings", 
      icon: "⚙️", 
      color: "#ed8936",
      description: "Configure library rules"
    }
  ];

  const statsCards = [
    { 
      title: "Total Books", 
      value: stats.totalBooks.toLocaleString(), 
      icon: "📚", 
      color: "#667eea",
      bg: "rgba(102, 126, 234, 0.1)"
    },
    { 
      title: "Total Teachers", 
      value: stats.totalTeachers.toLocaleString(), 
      icon: "👩‍🏫", 
      color: "#48bb78",
      bg: "rgba(72, 187, 120, 0.1)"
    },
    { 
      title: "Books Issued", 
      value: stats.booksIssued.toLocaleString(), 
      icon: "📖", 
      color: "#ed8936",
      bg: "rgba(237, 137, 54, 0.1)"
    },
    { 
      title: "Pending Returns", 
      value: stats.pendingReturns.toLocaleString(), 
      icon: "⏳", 
      color: "#e53e3e",
      bg: "rgba(229, 62, 62, 0.1)"
    },
    { 
      title: "Total Fine", 
      value: `₹${stats.totalFine.toLocaleString()}`, 
      icon: "💰", 
      color: "#d69e2e",
      bg: "rgba(214, 158, 46, 0.1)"
    },
    { 
      title: "Transactions", 
      value: totalTransactions.toLocaleString(), 
      icon: "📋", 
      color: "#38b2ac",
      bg: "rgba(56, 178, 172, 0.1)"
    },
    { 
      title: "E-Resources", 
      value: elibraryCount.toLocaleString(), 
      icon: "📖", 
      color: "#9f7aea",
      bg: "rgba(159, 122, 234, 0.1)"
    }
  ];

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loader}></div>
        <p>Loading Dashboard...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Main Content */}
      <main style={styles.main}>
        {/* Header */}
        <header style={styles.header}>
          <div style={styles.headerLeft}>
            <h1 style={styles.welcomeText}>
              {greeting}, {adminName}! 👋
            </h1>
            <p style={styles.dateText}>
              {currentTime.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
          <div style={styles.headerRight}>
            <div style={styles.adminProfile} onClick={() => setShowMobileMenu(!showMobileMenu)}>
              <div style={styles.adminAvatar}>
                {adminName.charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Mobile Menu Dropdown */}
        {showMobileMenu && (
          <div style={styles.mobileMenu}>
            <div style={styles.mobileMenuItem} onClick={() => { navigate("/profile"); setShowMobileMenu(false); }}>
              <span>👤</span> My Profile
            </div>
            <div style={styles.mobileMenuItem} onClick={() => { navigate("/settings"); setShowMobileMenu(false); }}>
              <span>⚙️</span> Settings
            </div>
            <div style={styles.mobileMenuItem} onClick={() => { handleLogout(); setShowMobileMenu(false); }}>
              <span>🚪</span> Logout
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div style={styles.statsGrid}>
          {statsCards.map((stat, index) => (
            <div key={index} style={{...styles.statCard, background: stat.bg}}>
              <div style={{...styles.statIcon, backgroundColor: stat.color}}>
                {stat.icon}
              </div>
              <div style={styles.statContent}>
                <h3 style={styles.statValue}>{stat.value}</h3>
                <p style={styles.statTitle}>{stat.title}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions Section */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div>
              <h2 style={styles.sectionTitle}>⚡ Quick Actions</h2>
              <p style={styles.sectionSubtitle}>Frequently used operations</p>
            </div>
          </div>
          
          <div style={styles.actionsGrid}>
            {quickActions.map((action, index) => (
              <div 
                key={index} 
                style={styles.actionCard}
                onClick={() => navigate(action.path)}
              >
                <div style={{...styles.actionIcon, backgroundColor: action.color}}>
                  {action.icon}
                </div>
                <div style={styles.actionContent}>
                  <h3 style={styles.actionName}>{action.name}</h3>
                  <p style={styles.actionDesc}>{action.description}</p>
                </div>
                <span style={styles.actionArrow}>→</span>
              </div>
            ))}
          </div>
        </section>

        {/* Top Books Section */}
        <div style={styles.topBooksCard}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>📚 Most Popular Books</h2>
          </div>
          
          <div style={styles.topBooksList}>
            {topBooks.map((book) => (
              <div key={book.rank} style={styles.topBookItem}>
                <div style={styles.topBookRank}>{book.rank}</div>
                <div style={styles.topBookInfo}>
                  <p style={styles.topBookName}>{book.name}</p>
                  <p style={styles.topBookStats}>{book.borrows} borrows</p>
                </div>
                <div style={styles.topBookProgress}>
                  <div style={{...styles.progressFill, width: `${Math.min((book.borrows / (topBooks[0]?.borrows || 1)) * 100, 100)}%`}}></div>
                </div>
              </div>
            ))}
          </div>
          
          <button 
            onClick={() => navigate("/books")} 
            style={styles.viewAllBtn}
          >
            View All Books →
          </button>
        </div>

        {/* Bottom Navigation Bar */}
        <div style={styles.bottomNav}>
          {bottomNavItems.map((item) => (
            <div
              key={item.path}
              style={{
                ...styles.bottomNavItem,
                ...(location.pathname === item.path && styles.bottomNavItemActive)
              }}
              onClick={() => navigate(item.path)}
            >
              <div style={styles.bottomNavIcon}>
                {location.pathname === item.path ? item.activeIcon : item.icon}
              </div>
              <span style={styles.bottomNavLabel}>{item.name}</span>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100dvh",
    height: "auto",
         overflowY: "auto",
    backgroundColor: "#f7fafc",
    fontFamily: "'Segoe UI', 'Inter', system-ui, sans-serif",
    position: "relative",
    paddingBottom: "70px",
    overflowY: "auto",  // Add this
    WebkitOverflowScrolling: "touch",  // Add thisoverflowY: "auto",  // Add this
    WebkitOverflowScrolling: "touch",  // Add this
  },

  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100dvh",
height: "auto",
overflowY: "auto",
    backgroundColor: "#f7fafc",
    overflowY: "auto",  // Add this
    WebkitOverflowScrolling: "touch",  // Add this
  },

  loader: {
    width: "50px",
    height: "50px",
    border: "3px solid #e2e8f0",
    borderTopColor: "#667eea",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },

  main: {
    flex: 1,
    padding: "16px",
    paddingBottom: "80px",
    maxWidth: "600px",
    margin: "0 auto",
    width: "100%",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "16px",
  },

  headerLeft: {
    flex: 1,
  },

  welcomeText: {
    fontSize: "20px",
    fontWeight: "700",
    margin: "0 0 4px 0",
    color: "#2d3748",
  },

  dateText: {
    fontSize: "12px",
    color: "#718096",
    margin: 0,
  },

  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },

  adminProfile: {
    cursor: "pointer",
  },

  adminAvatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "18px",
  },

  mobileMenu: {
    position: "absolute",
    top: "70px",
    right: "16px",
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    zIndex: 1000,
    minWidth: "160px",
    overflow: "hidden",
  },

  mobileMenuItem: {
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "14px",
    color: "#2d3748",
    cursor: "pointer",
    transition: "background 0.2s ease",
    borderBottom: "1px solid #e2e8f0",
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "12px",
    marginBottom: "24px",
  },

  statCard: {
    padding: "14px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    cursor: "pointer",
  },

  statIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
  },

  statContent: {
    flex: 1,
  },

  statValue: {
    fontSize: "18px",
    fontWeight: "700",
    margin: "0 0 2px 0",
    color: "#2d3748",
  },

  statTitle: {
    fontSize: "11px",
    color: "#718096",
    margin: 0,
  },

  section: {
    marginBottom: "24px",
  },

  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    flexWrap: "wrap",
    gap: "8px",
  },

  sectionTitle: {
    fontSize: "18px",
    fontWeight: "600",
    margin: 0,
    color: "#2d3748",
  },

  sectionSubtitle: {
    fontSize: "12px",
    color: "#718096",
    margin: "2px 0 0 0",
  },

  actionsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
  },

  actionCard: {
    backgroundColor: "#fff",
    padding: "14px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    gap: "14px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    border: "1px solid #e2e8f0",
  },

  actionIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
  },

  actionContent: {
    flex: 1,
  },

  actionName: {
    fontSize: "15px",
    fontWeight: "600",
    margin: "0 0 2px 0",
    color: "#2d3748",
  },

  actionDesc: {
    fontSize: "11px",
    color: "#718096",
    margin: 0,
  },

  actionArrow: {
    fontSize: "18px",
    color: "#cbd5e0",
    transition: "transform 0.2s ease",
  },

  topBooksCard: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "20px",
    marginBottom: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    border: "1px solid #e2e8f0",
  },

  topBooksList: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginBottom: "20px",
  },

  topBookItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },

  topBookRank: {
    width: "28px",
    height: "28px",
    borderRadius: "8px",
    backgroundColor: "#667eea",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: "bold",
  },

  topBookInfo: {
    flex: 1,
  },

  topBookName: {
    fontSize: "14px",
    fontWeight: "500",
    margin: "0 0 2px 0",
    color: "#2d3748",
  },

  topBookStats: {
    fontSize: "11px",
    color: "#718096",
    margin: 0,
  },

  topBookProgress: {
    flex: 1,
    maxWidth: "100px",
    height: "6px",
    backgroundColor: "#e2e8f0",
    borderRadius: "3px",
    overflow: "hidden",
  },

  progressFill: {
    height: "100%",
    backgroundColor: "#667eea",
    borderRadius: "3px",
    transition: "width 0.3s ease",
  },

  viewAllBtn: {
    width: "100%",
    padding: "10px",
    backgroundColor: "#f7fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "500",
    color: "#667eea",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  bottomNav: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    padding: "8px 8px",
    boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
    zIndex: 100,
    borderTop: "1px solid #e2e8f0",
  },

  bottomNavItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    padding: "6px 8px",
    borderRadius: "8px",
    transition: "all 0.2s ease",
    flex: 1,
  },

  bottomNavItemActive: {
    color: "#667eea",
  },

  bottomNavIcon: {
    fontSize: "20px",
  },

  bottomNavLabel: {
    fontSize: "10px",
    fontWeight: "500",
  },
};

// Add global styles
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    -webkit-tap-highlight-color: transparent;
  }
  
  body {
    background-color: #f7fafc;
    overflow-x: hidden;
  }
  
  .stat-card:active {
    transform: scale(0.98);
  }
  
  .action-card:active {
    transform: scale(0.98);
  }
  
  .action-card:active .action-arrow {
    transform: translateX(4px);
    color: #667eea;
  }
  
  .mobile-menu-item:active {
    background-color: #f7fafc;
  }
  
  .bottom-nav-item:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 480px) {
    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
    }
    
    .stat-card {
      padding: 10px;
    }
    
    .stat-icon {
      width: 36px;
      height: 36px;
      font-size: 18px;
    }
    
    .stat-value {
      font-size: 16px;
    }
    
    .stat-title {
      font-size: 10px;
    }
    
    .bottom-nav-label {
      font-size: 9px;
    }
    
    .bottom-nav-icon {
      font-size: 18px;
    }
  }
  
  @media (min-width: 768px) {
    .main {
      padding: 24px;
      max-width: 800px;
    }
    
    .stats-grid {
      gap: 16px;
    }
    
    .actions-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  
  @media (min-width: 1024px) {
    .main {
      max-width: 1000px;
    }
    
    .stats-grid {
      gap: 20px;
    }
  }
`;
document.head.appendChild(styleSheet);